<template>
  <mf-loading loader-type="dialog" :boolean-loader="loading">
    <v-dialog :value="internalValue" width="1000px" persistent @input="$emit('input', false)">
      <v-card v-if="internalValue" class="dialog-content">
        <v-card-title>
          <div>
            <span class="section-title">Editar loja</span>
            <p class="subtitle ma-0">
              Você está editando a loja de ID: <strong>{{ lazyUnit.id_erp }}</strong>
            </p>
          </div>
        </v-card-title>
        <v-card-text class="px-8 pt-2">
          <v-row>
            <v-col class="pa-0 pr-2" cols="5">
              <v-text-field v-model="lazyUnit.name" dense label="Nome" :autofocus="!(isAdmin && adminView)" :error-messages="errorMessages.name" outlined />
            </v-col>

            <v-col class="pa-0 pr-2" cols="3">
              <v-text-field v-model="lazyUnit.cnpj" v-mask="'##.###.###/####-##'" label="CNPJ" disabled :error-messages="errorMessages.cnpj" outlined dense />
            </v-col>

            <v-col class="pa-0" cols="3">
              <v-text-field
                v-model="lazyUnit.contact"
                v-mask="['+55 (##) #####-####', '+55 (##) ####-####']"
                label="Contato"
                :error-messages="errorMessages.contact"
                outlined
                dense
              />
            </v-col>
            <v-col v-if="showFlags" class="pa-0 pr-2" cols="3">
              <v-select v-model="lazyUnit.retailer_flag_id" label="Bandeira" :items="flags" item-text="name" item-value="id" outlined dense />
            </v-col>
            <v-col class="pa-0 pr-2" :cols="showFlags ? 4 : 5">
              <v-text-field v-model="internalPostalCode" v-mask="'#####-###'" label="CEP" disabled outlined dense />
            </v-col>
            <v-col class="pa-0" :cols="showFlags ? 4 : 6">
              <v-select v-model="address.federative_unit" label="Estado" :items="estados" item-text="name" item-value="value" disabled outlined dense />
            </v-col>
            <v-col class="pa-0 pr-2" cols="6">
              <v-select v-model="address.city_name" label="Cidade" :items="cidadesFiltradas" item-text="Nome" item-value="Nome" disabled outlined dense />
            </v-col>
            <v-col class="pa-0" cols="5">
              <v-text-field v-model="address.neighborhood" dense label="Bairro" disabled outlined />
            </v-col>

            <v-col class="pa-0 pr-2" cols="5">
              <v-text-field v-model="address.street" dense label="Rua" disabled outlined />
            </v-col>
            <v-col class="pa-0 pr-2" cols="2">
              <v-text-field v-model="address.number" dense label="Número" disabled outlined />
            </v-col>
            <v-col class="pa-0 pr-2" cols="2">
              <v-text-field v-model="latitude" dense label="Latitude" disabled outlined suffix="°" />
            </v-col>
            <v-col class="pa-0" cols="2">
              <v-text-field v-model="longitude" dense label="Longitude" disabled outlined suffix="°" />
            </v-col>

            <v-col class="pa-0" cols="11">
              <v-text-field v-model="internalFullAddress" dense label="Endereço completo" :error-messages="errorMessages.address" outlined />
            </v-col>

            <v-col class="pa-0 mb-4" cols="11">
              <div class="d-flex align-center">
                <mf-select
                  v-model="lazyUnit.unit_group_id"
                  hide-details
                  :value="lazyUnit.unit_group_id"
                  :items="unitGroups"
                  label="Selecione um grupo de precificação"
                  class="mr-2 price-group-input"
                  item-text="name"
                  item-value="_id"
                  dense
                  :no-data-text="!allUnitGroups.length ? 'Nenhum grupo cadastrado' : 'Nenhum grupo encontrado'"
                >
                  <v-list-item>
                    <v-list-item-content>
                      <mf-text-input v-model="searchTerm" hide-details dense outlined placeholder="Busque um grupo de precificação" />
                    </v-list-item-content>
                  </v-list-item>
                  <div class="py-0 px-4">
                    <v-divider class="ma-0"></v-divider>
                  </div>
                </mf-select>
                <mf-tooltip top :disabled="!lazyUnit.unit_group_id">
                  <template #activator="{ on }">
                    <mf-button :disabled="!lazyUnit.unit_group_id" icon="cancel" type="icon" color="vermelho-d20" @click="removeGroup" />
                  </template>
                  <span>Remover grupo de precificação</span>
                </mf-tooltip>
              </div>
            </v-col>

            <v-col class="pa-0" cols="11">
              <span class="table-card-title">Configurações</span>
              <div class="ecommerce-switch mt-3">
                <mf-toggle v-model="lazyUnit.mobile" class="ml-1" label="Disponível no aplicativo" :disabled="!$getAllowedModules()['mobile']" hide-details />
                <v-tooltip right max-width="280px">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <mf-icon small color="cinza-0">info</mf-icon>
                    </span>
                  </template>
                  <span> Para adquirir ou cancelar o aplicativo em uma loja entre em contato com seu consultor </span>
                </v-tooltip>
              </div>
              <div class="ecommerce-switch mt-3">
                <mf-toggle v-model="lazyUnit.ecommerce_v2_active" class="ml-1" disabled label="Possui e-commerce" hide-details />
                <v-tooltip right max-width="280px">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <mf-icon small color="cinza-d10" v-on="on">info</mf-icon>
                    </span>
                  </template>
                  <span> Para adquirir ou cancelar o e-commerce em uma loja, entre em contato com seu consultor </span>
                </v-tooltip>
              </div>
            </v-col>

            <v-col class="pa-0 mt-4" cols="12">
              <span class="table-card-title">Horários de funcionamento da loja física</span>
            </v-col>

            <v-col cols="12" class="pa-0">
              <v-row class="office-hours-content">
                <v-col v-for="[officeHoursDay, officeHours] in officeHoursDays" :key="officeHoursDay">
                  <v-row justify="center">
                    <span :class="[officeHours && officeHours.length ? '' : 'closed-office-hours']">{{ officeHoursTranslation[officeHoursDay] }}</span>
                  </v-row>
                  <v-row justify="center" class="office-hours-content-chips">
                    <v-chip v-for="({ start, finish }, index) in officeHours" :key="index" x-small class="office-hours-chip">
                      <span class="clickable" @click="toggleEditOfficeHour(officeHoursDay, index, { start, finish })">{{ start }} às {{ finish }}</span>
                      <mf-icon x-small @click="officeHours.splice(index, 1)">clear</mf-icon>
                    </v-chip>
                  </v-row>
                  <v-row v-if="officeHours.length < 2" justify="center" class="pt-1 office-hours-content-new">
                    <mf-icon class="cursor-pointer" small @click="toggleEditOfficeHour(officeHoursDay, officeHours.length)">add_circle</mf-icon>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <mf-action-buttons
          class="mx-2 pb-2"
          :primary-button="{
            text: 'Salvar',
            action: save,
            isVisible: true,
            isLoading: saving,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: cancel,
            isVisible: true,
            isDisabled: saving,
          }"
        />
      </v-card>
      <dialog-time-range
        v-if="settingOfficeHours"
        v-model="settingOfficeHours"
        :initialStart="selectedOfficeHoursStart"
        :initialFinish="selectedOfficeHoursFinish"
        :title="settingOfficeHoursTitle"
        :save="setOfficeHours"
      />
    </v-dialog>
  </mf-loading>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { getDataFromViaCep } from '@/helpers/cep'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import { notOnlySpace, validCnpj } from '@/helpers/validators.js'
import { data as cidades } from '@/modules/configuracoes/assets/cidades.json'
import { data as estados } from '@/modules/configuracoes/assets/estados.json'
import { GET_UNIT_GROUPS, QUERY_GET_UNIT_FLAGS } from '@/modules/configuracoes/lojas/graphql'
import { sanitizeUnitGroups } from '@/helpers/units'

const DEFAULT_ADDRESS = {
  postal_code: '',
  federative_unit: '',
  city_name: '',
  neighborhood: '',
  street: '',
  number: '',
}

export default {
  components: {
    DialogTimeRange: () => import('@/components/DialogTimeRange.vue'),
  },
  props: {
    saveCallback: Function,
    value: Boolean,
    unit: Object,
    adminView: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    lazyValue: false,
    lazyUnit: {},
    saving: false,
    settingOfficeHours: false,
    selectedOfficeHoursDay: null,
    selectedOfficeHoursPosition: null,
    selectedOfficeHoursStart: null,
    selectedOfficeHoursFinish: null,
    latitude: '',
    longitude: '',
    localFullAddress: '',
    cidades: cidades,
    estados: estados,
    fullAddressTouched: false,
    postalCodeTouched: false,
    loadingCep: false,
    address: DEFAULT_ADDRESS,
    loading: false,
    selectedUnitGroup: '',
    searchTerm: '',
    allUnitGroups: [],
    lastGroupId: '',
    selectedFlag: '',
    flags: [],
  }),
  computed: {
    showFlags() {
      return this.flags.length >= 1
    },
    unitGroups() {
      return this.allUnitGroups.filter((item) => item.name.includes(this.searchTerm))
    },
    isAdminAndIsNew() {
      return this.isAdmin && this.adminView && this.isNew
    },
    internalPostalCode: {
      get() {
        return this.address.postal_code
      },
      set(value) {
        const newRaw = value.replace(/[^\d]+/g, '')
        const oldRaw = this.address.postal_code.replace(/[^\d]+/g, '')
        if (newRaw !== oldRaw) {
          this.postalCodeTouched = true
        }
        this.address.postal_code = value
      },
    },
    internalFullAddress: {
      get() {
        if (this.fullAddressTouched) {
          return this.localFullAddress
        }
        if (this.lazyUnit.address) {
          return this.lazyUnit.address
        }
        return this.defaultFullAddress
      },
      set(value) {
        this.fullAddressTouched = true
        this.localFullAddress = value
      },
    },
    internalValue: {
      get() {
        return this.lazyValue
      },
      set(value) {
        this.lazyValue = value
        this.$emit('input', value)
      },
    },
    defaultFullAddress() {
      const fullAddressComponents = []
      if (this.address.street) {
        fullAddressComponents.push(this.address.street)
      }

      if (this.address.number || this.address.neighborhood) {
        fullAddressComponents.push([this.address.number, this.address.neighborhood].filter((el) => Boolean(el)).join(' - '))
      }

      if (this.address.city_name || this.address.federative_unit) {
        fullAddressComponents.push([this.address.city_name, this.address.federative_unit].filter((el) => Boolean(el)).join(' - '))
      }

      if (this.address.postal_code) {
        fullAddressComponents.push(this.address.postal_code)
      }
      return fullAddressComponents.join(', ')
    },
    cidadesFiltradas() {
      return this.cidades.filter((cidade) => cidade.Uf === this.address.federative_unit)
    },
    isAdmin() {
      return this.$getIsAdmin()
    },
    settingOfficeHoursTitle() {
      return `Horário para ${this.officeHoursTranslation[this.selectedOfficeHoursDay]}`
    },
    officeHoursTranslation() {
      return {
        monday: 'Segunda',
        tuesday: 'Terça',
        wednesday: 'Quarta',
        thursday: 'Quinta',
        friday: 'Sexta',
        saturday: 'Sábado',
        sunday: 'Domingo',
        holidays: 'Feriados',
      }
    },
    officeHoursDays() {
      return this.lazyUnit.office_hours ? Object.entries(this.lazyUnit.office_hours).filter(([day]) => day !== '__typename') : []
    },
    errorMessages() {
      const fields = ['id_erp', 'cnpj', 'name', 'contact']
      const errors = fields.reduce((errors, field) => {
        if (this.$v.lazyUnit[field] && this.$v.lazyUnit[field].$error) {
          if (this.$v.lazyUnit[field].required === false) errors[field] = 'Campo obrigatório.'
          else if (this.$v.lazyUnit[field].notOnlySpace === false) errors[field] = 'Não é permitido salvar apenas espaços.'
        } else {
          errors[field] = ''
        }
        return errors
      }, {})
      if (this.$v.lazyUnit.contact.minLength === false || this.$v.lazyUnit.contact.maxLength === false) errors.contact = 'Número inválido.'
      if (!errors.cnpj && !this.$v.lazyUnit.cnpj.validCnpj) errors.cnpj = 'CNPJ inválido.'
      if (this.$v.internalFullAddress.$error) errors['address'] = 'Endereço completo inválido.'
      if (this.$v.address.postal_code.$error) errors['postal_code'] = 'CEP inválido.'
      if (this.$v.address.federative_unit.$error) errors['federative_unit'] = 'Estado inválido.'
      if (this.$v.address.city_name.$error && !this.$v.address.federative_unit.$error) errors['city_name'] = 'Cidade inválida.'
      if (this.$v.address.neighborhood.$error) errors['neighborhood'] = 'Bairro inválido.'
      if (this.$v.address.street.$error) errors['street'] = 'Rua inválida.'
      if (this.$v.address.number.$error) errors['number'] = 'Número inválido.'
      if (this.$v.latitude.$error) errors['latitude'] = 'Latitude inválida.'
      if (this.$v.longitude.$error) errors['longitude'] = 'Longitude inválida.'
      return errors
    },
  },
  watch: {
    value(value) {
      this.lazyValue = value
      // Reset field
      if (value) {
        this.setupUnit(this.unit)
      }
    },
    unit(value) {
      this.setupUnit(value)
    },
    async 'address.postal_code'(value) {
      const raw = value.replace(/[^\d]+/g, '')
      if (raw.length !== 8 || !this.postalCodeTouched) {
        return
      }
      try {
        this.loadingCep = true
        const cepData = await getDataFromViaCep(raw)
        this.address.federative_unit = cepData.uf || ''
        this.address.city_name = cepData.localidade || ''
        this.address.neighborhood = cepData.bairro || ''
        this.address.street = cepData.logradouro || ''
      } catch (e) {
        this.$snackbar({ message: 'Falha ao procurar o CEP' })
      }
      this.loadingCep = false
    },
  },
  async mounted() {
    this.loading = true
    this.lazyValue = this.value
    this.setupUnit(this.unit)
    await this.getFlags()
    await this.getUnitGroups()
  },
  methods: {
    removeGroup() {
      this.lastGroupId = this.lazyUnit.unit_group_id
      const undoFn = () => {
        this.lazyUnit.unit_group_id = this.lastGroupId
      }
      this.$snackbar({ message: 'Grupo de lojas removido', snackbarColor: 'error', btnText: 'Desfazer', actionCallback: undoFn })
      this.lazyUnit.unit_group_id = null
    },
    async getUnitGroups() {
      try {
        const {
          data: {
            unitGroups: { unit_groups },
          },
        } = await this.$apollo.query({
          query: GET_UNIT_GROUPS,
          fetchPolicy: 'no-cache',
        })
        this.allUnitGroups = sanitizeUnitGroups(unit_groups)
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    async getFlags() {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_GET_UNIT_FLAGS,
          fetchPolicy: 'no-cache',
        })
        this.flags = data.retailerFlags
      } catch (error) {
        console.log(error)
      }
    },
    setupUnit(unit) {
      this.postalCodeTouched = false
      this.lazyUnit = cloneDeep({
        ...unit,
        unit_group_id: (unit.unit_group && unit.unit_group._id) || '',
      })
      if (this.lazyUnit?.location?.coordinates) {
        this.latitude = String(this.lazyUnit.location.coordinates[0]).replace('.', ',')
        this.longitude = String(this.lazyUnit.location.coordinates[1]).replace('.', ',')
      }
      if (this.lazyUnit?.address_components) {
        this.address = this.lazyUnit.address_components
      } else {
        this.address = cloneDeep(DEFAULT_ADDRESS)
      }
      if (!this.lazyUnit?.office_hours) {
        this.lazyUnit.office_hours = {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: [],
          holidays: [],
        }
      }
    },
    setOfficeHours(start, finish) {
      this.lazyUnit.office_hours[this.selectedOfficeHoursDay][this.selectedOfficeHoursPosition] = {
        start,
        finish,
      }
    },
    toggleEditOfficeHour(officeHoursDay, position, { start = null, finish = null } = {}) {
      this.settingOfficeHours = true
      this.selectedOfficeHoursDay = officeHoursDay
      this.selectedOfficeHoursPosition = position
      this.selectedOfficeHoursStart = start
      this.selectedOfficeHoursFinish = finish
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        return this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
      }
      const confirmed = await this.$confirm({
        title: 'Salvar alterações',
        message: 'Todas as alterações serão disponibilizadas no aplicativo imediatamente. Deseja realmente editar a unidade?',
        confirmColor: 'primary',
        confirmText: 'Salvar',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return
      this.saving = true
      try {
        this.lazyUnit.location = {
          type: 'Point',
          coordinates: [parseFloat(this.latitude.replace(',', '.')), parseFloat(this.longitude.replace(',', '.'))],
        }
        this.lazyUnit.address_components = cloneDeep(this.address)
        this.lazyUnit.address_components.postal_code = this.lazyUnit.address_components.postal_code.replace(/[^\d]+/g, '')
        this.lazyUnit.cnpj = this.lazyUnit.cnpj.replace(/[^\d]+/g, '')
        this.lazyUnit.address = this.internalFullAddress
        this.lazyUnit.mobile = this.lazyUnit.mobile || false
        await this.saveCallback(this.lazyUnit)
      } catch (e) {
        this.$snackbar({ message: 'Falha ao salvar alterações', snackbarColor: 'error' })
      }
      this.saving = false
      this.internalValue = false
      this.$emit('close')
    },
    cancel() {
      this.internalValue = false
      this.lazyUnit = {}
      this.$emit('close')
    },
    removeOfficeHoursInterval(officeHours, index) {},
  },
  validations: {
    latitude: {
      validNumber: (latitude) => latitude && !isNaN(latitude.replace(',', '.')),
    },
    longitude: {
      validNumber: (longitude) => longitude && !isNaN(longitude.replace(',', '.')),
    },
    lazyUnit: {
      id_erp: { required, notOnlySpace },
      name: { required, notOnlySpace },
      cnpj: { required, validCnpj },
      contact: { required, notOnlySpace, minLength: minLength(18), maxLength: maxLength(19) },
    },
    internalFullAddress: { required, notOnlySpace },
    address: {
      postal_code: { required, notOnlySpace, minLength: minLength(9), maxLength: maxLength(9) },
      federative_unit: { required, notOnlySpace },
      city_name: { required, notOnlySpace },
      neighborhood: { required, notOnlySpace },
      street: { required, notOnlySpace },
      number: { required, notOnlySpace },
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-content {
  .block-title {
    opacity: 0.8;
    font-size: 18px;
    font-weight: bold;
    color: $preto;
  }
  .detail-title {
    opacity: 0.8;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.2;
    letter-spacing: 0.24px;
    color: $preto;
    margin-bottom: 8px;
  }

  .closed-office-hours {
    text-decoration: line-through;
  }
  .office-hours-content-chips {
    .office-hours-chip {
      margin-bottom: 3px;
      padding-right: 0px;
      padding-left: 10px;
      span {
        padding-right: 5px;
      }
      .clickable {
        cursor: pointer;
      }
    }
  }
  .office-hours-content-new {
    padding-top: 5px;
    .office-hours-content-new-input {
      font-size: 0.75em;
      border: 1px solid $cinza-l70;
      height: 20px;
      width: 50px;
      display: block;
    }
    i {
      padding-left: 5px;
    }
  }

  & strong {
    color: $marca-1;
  }

  & .ecommerce-switch {
    display: flex;
    align-items: center;
    gap: 10px;
    ::v-deep .v-input--selection-controls {
      padding: 0;
      margin: 0;
    }
  }
}

.delete_icon:hover {
  .mf-icon {
    color: $vermelho-d20 !important;
  }
}

::v-deep .price-group-input {
  .v-divider {
    max-width: calc(100% - 32px) !important;
  }
}
</style>
